import React, { useEffect, useState, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import '@/App.css';
import SandboxBanner from './components/SandboxBanner';
import TradieChatPopup from './components/TradieChatPopup';
import CustomerChatNotifier from './components/CustomerChatNotifier';
import NotificationManager from './components/NotificationManager';
import CookieConsent from './components/CookieConsent';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import ReviewPromptOverlay from './components/ReviewPromptOverlay';
import { Toaster } from '@/components/ui/sonner';
import ForcePasswordChange from './pages/ForcePasswordChange';
import { DialogProvider } from './components/DialogProvider';
import { useAnalytics } from './hooks/useAnalytics';
import { registerServiceWorker } from './utils/swRegister';

// Route configs
import publicRoutes from './routes/publicRoutes';
import adminRoutes from './routes/adminRoutes';
import authRoutes from './routes/authRoutes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext();
export const AnalyticsContext = React.createContext();

function AnalyticsProvider({ children, user }) {
  const { trackEvent } = useAnalytics(user);
  return (
    <AnalyticsContext.Provider value={{ trackEvent }}>
      {children}
    </AnalyticsContext.Provider>
  );
}

// Loading fallback for lazy-loaded routes
function RouteLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-3 border-mlt-orange border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-mlt-text-muted">Loading...</p>
      </div>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Suppress ResizeObserver errors (benign error from Radix UI components)
  useEffect(() => {
    const errorHandler = (e) => {
      if (e.message && (
          e.message.includes('ResizeObserver loop') || 
          e.message.includes('ResizeObserver loop completed with undelivered notifications')
      )) {
        e.stopImmediatePropagation();
        return;
      }
    };
    window.addEventListener('error', errorHandler);
    return () => window.removeEventListener('error', errorHandler);
  }, []);

  useEffect(() => {
    const hash = window.location.hash;
    const params = new URLSearchParams(window.location.search);
    let sessionId = params.get('session_id');
    if (!sessionId && hash && hash.includes('session_id=')) {
      sessionId = hash.split('session_id=')[1].split('&')[0];
    }
    if (sessionId) {
      processSession(sessionId);
    } else {
      checkExistingSession();
    }
  }, []);

  const processSession = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/auth/session`, {
        headers: { 'X-Session-ID': sessionId },
        withCredentials: true
      });
      
      if (response.data.requires_2fa) {
        window.location.href = `/verify-otp?user=${encodeURIComponent(JSON.stringify(response.data.user))}&session=${sessionId}`;
        return;
      }
      
      setUser(response.data.user);
      const next = new URL(window.location.href);
      next.searchParams.delete('session_id');
      next.hash = '';
      window.history.replaceState({}, document.title, `${next.pathname}${next.search}`);
    } catch (error) {
      console.error('Session processing error:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkExistingSession = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      // No valid session
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Auto-logout after a period of inactivity (applies to every role). Any user
  // activity resets the timer; after INACTIVITY_TIMEOUT_MS with no activity the
  // session is ended server-side and the user is sent to the login page.
  useEffect(() => {
    if (!user) return;
    const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;   // 30 minutes
    let timer;
    const doAutoLogout = async () => {
      try {
        await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      } catch (e) { /* ignore — still clear local session */ }
      setUser(null);
      try { toast.info('You have been logged out due to inactivity.'); } catch (e) {}
      const isStaff = ['admin', 'agent', 'sales'].includes(user.role);
      window.location.href = isStaff ? '/admin/login?timeout=1' : '/login?timeout=1';
    };
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(doAutoLogout, INACTIVITY_TIMEOUT_MS);
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(e => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, reset));
    };
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  const authenticatedRoutes = authRoutes(user);

  return (
    <AuthContext.Provider value={{ user, setUser, logout }}>
      <DialogProvider>
      <BrowserRouter>
        <AnalyticsProvider user={user}>
        <SandboxBanner />
        <Suspense fallback={<RouteLoader />}>
          {user?.must_change_password ? (
            /* Force password change before anything else is accessible */
            <ForcePasswordChange />
          ) : (
          <Routes>
            {/* Public Routes */}
            {publicRoutes.map(r => (
              <Route key={r.path} path={r.path} element={r.element} />
            ))}

            {/* Authenticated Routes */}
            {authenticatedRoutes.map(r => (
              <Route key={r.path} path={r.path} element={r.element} />
            ))}

            {/* Admin Routes */}
            {adminRoutes.map(r => (
              <Route key={r.path} path={r.path} element={r.element} />
            ))}
          </Routes>
          )}
        </Suspense>
        {/* Global Chat Popup for Tradies */}
        <TradieChatPopup />
        {/* Global message toasts for Customers */}
        <CustomerChatNotifier />
        {/* Global Notification Manager */}
        <NotificationManager />
        {/* Global Review Prompt — post-completion popup with snooze cadence */}
        <ReviewPromptOverlay />
        <CookieConsent />
        <PWAInstallPrompt />
        </AnalyticsProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
      </DialogProvider>
    </AuthContext.Provider>
  );
}

export default App;

// Register service worker for PWA
registerServiceWorker();
